import React, { createContext, useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';

const FarmOpenSectionsContext = createContext(null);

// Extracts the location index from a fieldName like "farmLocations[0].risks[1]"
const parseLocationIndex = (fieldName) => {
  const match = fieldName?.match(/farmLocations\[(\d+)\]/);
  return match ? parseInt(match[1], 10) : null;
};

export const FarmOpenSectionsProvider = ({ children }) => {
  const [openLocationIndices, setOpenLocationIndices] = useState(() => new Set([0]));
  const [openRisksByLocation, setOpenRisksByLocation] = useState(() => ({
    0: new Set(['farmLocations[0].risks[0]'])
  }));

  const toggleLocation = useCallback((index) => {
    setOpenLocationIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const addOpenLocation = useCallback((index) => {
    setOpenLocationIndices((prev) => {
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  }, []);

  const toggleRisk = useCallback((locationIndex, riskFieldName) => {
    setOpenRisksByLocation((prev) => {
      const currentSet = prev[locationIndex] || new Set();
      const next = new Set(currentSet);
      if (next.has(riskFieldName)) {
        next.delete(riskFieldName);
      } else {
        next.add(riskFieldName);
      }
      return { ...prev, [locationIndex]: next };
    });
  }, []);

  const addOpenRisk = useCallback((locationIndex, riskFieldName) => {
    setOpenRisksByLocation((prev) => {
      const currentSet = prev[locationIndex] || new Set();
      const next = new Set(currentSet);
      next.add(riskFieldName);
      return { ...prev, [locationIndex]: next };
    });
  }, []);

  const navigateTo = useCallback((locationIndex, riskId) => {
    setOpenLocationIndices(new Set([locationIndex]));

    if (riskId !== null) {
      // Focus the navigated risk at the target location, but preserve every other location's
      // open-risk state so it's still there when the user reopens that location.
      setOpenRisksByLocation((prev) => ({
        ...prev,
        [locationIndex]: new Set([riskId])
      }));
    } else {
      setOpenRisksByLocation((prev) => {
        const currentRisks = prev[locationIndex];
        if (currentRisks && currentRisks.size > 0) {
          return prev;
        }
        return { ...prev, [locationIndex]: new Set([`farmLocations[${locationIndex}].risks[0]`]) };
      });
    }
  }, []);

  // Called when a location is removed. Drops the deleted index from the open-state and shifts
  // any higher indices down by one to stay aligned with the spliced farmLocations array. Stored
  // risk fieldName strings (e.g. "farmLocations[3].risks[0]") embed the location index, so they
  // also need to be rewritten on shift.
  const removeLocation = useCallback((deletedIndex) => {
    setOpenLocationIndices((prev) => {
      const next = new Set();
      prev.forEach((i) => {
        if (i === deletedIndex) {
          return;
        }
        next.add(i > deletedIndex ? i - 1 : i);
      });
      return next;
    });

    setOpenRisksByLocation((prev) => {
      const next = {};
      Object.entries(prev).forEach(([keyStr, set]) => {
        const i = parseInt(keyStr, 10);
        if (i === deletedIndex) {
          return;
        }
        if (i < deletedIndex) {
          next[i] = set;
          return;
        }
        const newIndex = i - 1;
        const rewritten = new Set();
        set.forEach((fieldName) => {
          rewritten.add(fieldName.replace(/^farmLocations\[\d+\]/, `farmLocations[${newIndex}]`));
        });
        next[newIndex] = rewritten;
      });
      return next;
    });
  }, []);

  const resetToFirstRisk = useCallback((locationIndex) => {
    setOpenRisksByLocation((prev) => ({
      ...prev,
      [locationIndex]: new Set([`farmLocations[${locationIndex}].risks[0]`])
    }));
  }, []);

  return (
    <FarmOpenSectionsContext.Provider
      value={{
        openLocationIndices,
        openRisksByLocation,
        toggleLocation,
        addOpenLocation,
        toggleRisk,
        addOpenRisk,
        navigateTo,
        removeLocation,
        resetToFirstRisk
      }}
    >
      {children}
    </FarmOpenSectionsContext.Provider>
  );
};

FarmOpenSectionsProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useFarmOpenSections = (fieldName) => {
  const context = useContext(FarmOpenSectionsContext);
  if (!context) {
    throw new Error('useFarmOpenSections must be used within a FarmOpenSectionsProvider');
  }

  const locationIndex = fieldName ? parseLocationIndex(fieldName) : null;
  const isCurrentSectionOpen = fieldName
    ? (context.openRisksByLocation[locationIndex]?.has(fieldName) ?? false)
    : undefined;
  const toggleSection = fieldName ? () => context.toggleRisk(locationIndex, fieldName) : undefined;

  return { ...context, isCurrentSectionOpen, toggleSection };
};
